import { AiOutlineEdit, AiOutlineMail, AiOutlinePhone } from 'react-icons/ai';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '../../../components/Breadcrumb';
import DataContainer from '../../stores/components/DefaultApiConfig/DataContainer';
import EditAgency from '../components/EditAgency';
import { FaLocationArrow } from 'react-icons/fa';
import LoadingSpinner from '../../../components/LoadingSpinner';
import MerchantDetailStatus from '../../merchants/components/MerchantDetailStatus';
import { OwnerType } from '../../../common/constants';
import RuleDataContainer from '../../stores/components/DefaultRule/RuleDataContainer';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { toast } from 'react-toastify';
import { useGetAgencyByIdQuery } from '../agency.api-slice';
import { useGetAgencyDashboardQuery } from '../../dashboard/dashboard-api-slice';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const AgencyDashboardPage: React.FC = () => {
  const { id } = useParams();
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const { data: agency, isLoading, isSuccess } = useGetAgencyByIdQuery(id ?? skipToken);
  const { data: agencyStats, isLoading: isStatsLoading } = useGetAgencyDashboardQuery(id ?? skipToken);
  const { t } = useTranslation();

  const toggleEditModal = (): void => {
    setOpenEditModal(!openEditModal);
  };

  if (Boolean(isLoading) || isStatsLoading || id == null) {
    return <LoadingSpinner />;
  }

  if (Boolean(isSuccess) && agency === undefined) {
    toast('No such agency exists');
    return <></>;
  }

  const stats = [
    {
      id: 'merchantCount',
      title: 'Merchant',
      count: agencyStats?.merchantCount != null ? agencyStats.merchantCount : 0,
      link: `/admin/agencies/${id}/merchants`
    },
    {
      id: 'storeCount',
      title: 'Store',
      count: agencyStats?.storeCount != null ? agencyStats.storeCount : 0,
      link: `/admin/agencies/${id}/stores`
    },
    {
      id: 'userCount',
      title: 'User',
      count: agencyStats?.userCount != null ? agencyStats.userCount : 0,
      link: `/admin/agencies/${id}/users`
    },
    {
      id: 'deviceCount',
      title: 'Devices',
      count: agencyStats?.deviceCount != null ? agencyStats.deviceCount : 0,
      link: `/admin/agencies/${id}/devices`
    }
  ];

  return (
    <>
      {agency != null && <EditAgency item={agency} isOpen={openEditModal} toggleEditModal={toggleEditModal} />}
      {agency != null && (
        <div>
          <div className="pb-4">
            <h1>Dashboard</h1>
            <Breadcrumb
              items={[
                { label: t('common.adminBreadcrumbLabel') },
                { label: t('agencies.list.breadcrumbLabel') },
                { label: t('agencies.dashboard.breadcrumbLabel') }
              ]}
            />
          </div>
          <div className="flex w-full">
            <div className="w-1/2 flex flex-col px-4 bg-gray-50 py-4 rounded-lg">
              <div className="w-full flex justify-between items-center pb-4 border-b-2">
                <span className="font-xl font-bold text-xl text-center">{agency.name}</span>
                <div
                  className="flex py-2 px-4 text-gray-50 bg-[#1D8A43] text-center rounded-lg"
                  onClick={toggleEditModal}
                >
                  <AiOutlineEdit size={24} />
                  <button className="text-sm">Edit</button>
                </div>
              </div>

              {/* Detail of Store */}
              <div className="w-full flex">
                <div className="w-1/2">
                  <div className="flex justify-between px-4 py-2">
                    <div className="flex space-x-4 justify-between items-center">
                      <FaLocationArrow size={16} />
                      <span className="">{agency.address}</span>
                    </div>
                  </div>
                  <div className="flex justify-between px-4 py-2">
                    <div className="flex space-x-4 justify-between items-center">
                      <AiOutlineMail size={16} />
                      <span className="">{agency.email}</span>
                    </div>
                  </div>
                  <div className="flex justify-between px-4 py-2">
                    <div className="flex space-x-4 justify-between items-center">
                      <AiOutlinePhone size={16} />
                      <span className="">{agency.phone}</span>
                    </div>
                  </div>
                </div>
                {/* Status */}
                <div className="w-1/2 pl-16">
                  <div className="flex justify-between px-4 py-2">
                    <div className="flex space-x-4 justify-between items-center">
                      <MerchantDetailStatus status={agency.isApproved} title="Approved" />
                    </div>
                  </div>
                  <div className="flex justify-between px-4 py-2">
                    <div className="flex space-x-4 justify-between items-center">
                      <MerchantDetailStatus status={agency.isActive} title="Status" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stat Cards */}
            <div className="w-1/2 flex flex-col">
              <div className="w-full flex flex-col md:flex-row justify-between space-y-2 md:space-y-0  space-x-0 md:space-x-2 ml-2">
                {stats.slice(0, 2).map((stat, i) => {
                  return (
                    <div
                      key={i}
                      className={`sm:w-full md:w-1/${stats.length} h-24 px-4 py-2 flex flex-col justify-between bg-gray-100 rounded-md`}
                    >
                      <span className="text-4xl text-center px-2">{stat.count}</span>
                      <Link to={stat.link} className="text-md text-center hover:text-[#1D8A43]">
                        <span>{stat.title}</span>
                      </Link>
                    </div>
                  );
                })}
              </div>
              <div className="w-full flex flex-col md:flex-row justify-between space-y-2 md:space-y-0  space-x-0 md:space-x-2 m-2">
                {stats.slice(2, 4).map((stat) => {
                  return (
                    <div
                      key={stat.id}
                      className={`sm:w-full md:w-1/${stats.length} h-24 px-4 py-2 flex flex-col justify-between bg-gray-100 rounded-md`}
                    >
                      <span className="text-4xl text-center px-2">{stat.count}</span>
                      <Link to={stat.link} className="text-md text-center hover:text-[#1D8A43]">
                        <span>{stat.title}</span>
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex w-full space-x-2">
            <div className="w-1/2 flex flex-col px-4 bg-gray-50 py-4 rounded-lg mt-2">
              <RuleDataContainer ownerEntityId={agency.id} ownerEntityType={OwnerType.AGENCY} />
            </div>
            <div className="w-1/2 flex flex-col px-4 bg-gray-50 py-4 rounded-lg mt-2">
              <DataContainer ownerType={OwnerType.AGENCY} ownerEntity={agency} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgencyDashboardPage;
